import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface User {
  id: string;
  name: string;
  initials: string;
  email: string;
  role: 'student' | 'instructor' | 'admin';
  department: string;
  jobTitle: string;
  status: 'Active' | 'Inactive';
  lastLogin: string;
  coursesCompleted: number;
  coursesActive: number;
  createdAt?: string;
  avatar?: string;
  phone?: string;
  location?: string;
  manager?: string;
  joinDate?: string;
  progress?: number;
}

export interface UserDetail extends User {
  firstName: string;
  lastName: string;
  roleId: number;
  joinDate: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  organization?: string;
  totalCourses: number;
}

export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  newThisMonth: number;
}

export interface DeactivationLog {
  id: string;
  userId: string;
  userName: string;
  department: string;
  deactivatedDate: string;
  deactivatedBy: string;
  reason: string;
  reactivatedAt?: string;
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateUserDTO {
  firstName: string;
  lastName: string;
  email: string;
  role?: 'student' | 'instructor' | 'admin';
  department?: string;
  jobTitle?: string;
}

export interface UpdateUserDTO {
  firstName?: string;
  lastName?: string;
  department?: string;
  jobTitle?: string;
  status?: 'Active' | 'Inactive';
}

export interface BulkImportUser {
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
  department?: string;
  jobTitle?: string;
}

// ===== API RESPONSE INTERFACES =====

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: Pagination;
}

// ===== SERVICE FUNCTIONS =====

/**
 * Get all users with filtering and pagination
 */
export const getUserDirectory = async (
  params: {
    search?: string;
    status?: string;
    department?: string;
    role?: string;
    page?: number;
    limit?: number;
  } = {}
): Promise<{
  success: boolean;
  data?: User[];
  pagination?: Pagination;
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.department) queryParams.append('department', params.department);
    if (params.role) queryParams.append('role', params.role);
    queryParams.append('page', String(params.page || 1));
    queryParams.append('limit', String(params.limit || 10));

    const response = await httpClient.get<ApiResponse<User[]>>(
      `/super-admin/users?${queryParams.toString()}`
    );

    return {
      success: true,
      data: response.data || [],
      pagination: response.pagination,
      error: null,
    };
  } catch (error: any) {
    console.error('SuperAdminUsersService: Error fetching users:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch users' },
    };
  }
};

/**
 * Get user statistics
 */
export const getUserStats = async (): Promise<{
  success: boolean;
  data?: UserStats;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<UserStats>>(
      '/super-admin/users/stats'
    );

    return {
      success: true,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('SuperAdminUsersService: Error fetching user stats:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch user statistics' },
    };
  }
};

/**
 * Get single user by ID
 */
export const getUserById = async (
  userId: string
): Promise<{
  success: boolean;
  data?: UserDetail;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<UserDetail>>(
      `/super-admin/users/${userId}`
    );

    return {
      success: true,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('SuperAdminUsersService: Error fetching user:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch user' },
    };
  }
};

/**
 * Create a new user
 */
export const createUser = async (
  userData: CreateUserDTO
): Promise<{
  success: boolean;
  data?: { userId: string; generatedPassword: string };
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<
      ApiResponse<{ userId: string; generatedPassword: string }>
    >('/super-admin/users', userData);

    return {
      success: response.success,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('SuperAdminUsersService: Error creating user:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to create user' },
    };
  }
};

/**
 * Update user
 */
export const updateUser = async (
  userId: string,
  userData: UpdateUserDTO
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.put<ApiResponse<null>>(
      `/super-admin/users/${userId}`,
      userData
    );

    return {
      success: response.success,
      message: response.message || 'User updated successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('SuperAdminUsersService: Error updating user:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to update user' },
    };
  }
};

/**
 * Deactivate user
 */
export const deactivateUser = async (
  userId: string,
  reason: string
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<null>>(
      `/super-admin/users/${userId}/deactivate`,
      { reason }
    );

    return {
      success: response.success,
      message: response.message || 'User deactivated successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('SuperAdminUsersService: Error deactivating user:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to deactivate user' },
    };
  }
};

/**
 * Reactivate user
 */
export const reactivateUser = async (
  userId: string
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<null>>(
      `/super-admin/users/${userId}/reactivate`
    );

    return {
      success: response.success,
      message: response.message || 'User reactivated successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('SuperAdminUsersService: Error reactivating user:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to reactivate user' },
    };
  }
};

/**
 * Get deactivation log
 */
export const getDeactivationLogs = async (
  page: number = 1,
  limit: number = 10
): Promise<{
  success: boolean;
  data?: DeactivationLog[];
  pagination?: Pagination;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<DeactivationLog[]>>(
      `/super-admin/users/deactivation-log?page=${page}&limit=${limit}`
    );

    return {
      success: true,
      data: response.data || [],
      pagination: response.pagination,
      error: null,
    };
  } catch (error: any) {
    console.error('SuperAdminUsersService: Error fetching deactivation logs:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch deactivation logs' },
    };
  }
};

/**
 * Get departments for filter dropdown
 */
export const getDepartments = async (): Promise<{
  success: boolean;
  data?: string[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<string[]>>(
      '/super-admin/users/departments'
    );

    return {
      success: true,
      data: response.data || [],
      error: null,
    };
  } catch (error: any) {
    console.error('SuperAdminUsersService: Error fetching departments:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch departments' },
    };
  }
};

/**
 * Bulk import users
 */
export const bulkImportUsers = async (
  users: BulkImportUser[]
): Promise<{
  success: boolean;
  data?: { success: number; failed: number; errors: Array<{ email: string; error: string }> };
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<
      ApiResponse<{ success: number; failed: number; errors: Array<{ email: string; error: string }> }>
    >('/super-admin/users/bulk-import', { users });

    return {
      success: true,
      data: response.data,
      message: response.message,
      error: null,
    };
  } catch (error: any) {
    console.error('SuperAdminUsersService: Error importing users:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to import users' },
    };
  }
};

/**
 * Delete user (soft delete with is_delete=1)
 */
export const deleteUser = async (
  userId: string
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.delete<ApiResponse<null>>(
      `/super-admin/users/${userId}`
    );

    return {
      success: response.success,
      message: response.message || 'User deleted successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('SuperAdminUsersService: Error deleting user:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to delete user' },
    };
  }
};
